import { matchSorter } from 'match-sorter';
import { useLayoutEffect } from 'react';
import { createStore, useStore } from 'zustand';

export type FilterOption = {
  label: string;
  value: string | number;
  code?: string;
  [key: string]: any;
};

type NormalizedFilterOption = FilterOption & {
  normalizedLabel: string;
  normalizedValue: string;
  normalizedCode: string;
};

type FilterStore = {
  keyword: string;
  options: NormalizedFilterOption[];
  filteredOptions: NormalizedFilterOption[];
  handleChange: (value: string) => void;
  setOptions: (options: FilterOption[]) => void;
};

const storeRegistry = new Map<
  string | symbol,
  ReturnType<typeof createFilterStore>
>();
const storeRegistryFull = new Map<
  string | symbol,
  ReturnType<typeof createFilterStoreFull>
>();

export function removeVietnameseTones(str: string) {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');
}

const normalizeOptions = (options: FilterOption[]) =>
  options.map(option => ({
    ...option,
    normalizedLabel: removeVietnameseTones(
      option.label?.toString().toLowerCase() || '',
    ),
    normalizedValue: removeVietnameseTones(
      option.value?.toString().toLowerCase() || '',
    ),
    normalizedCode: removeVietnameseTones(
      option.code?.toString().toLowerCase() || '',
    ),
  }));

function createFilterStore() {
  return createStore<FilterStore>((set, get) => ({
    keyword: '',
    options: [],
    filteredOptions: [],
    setOptions: (options: FilterOption[]) => {
      const normalized = normalizeOptions(options);
      set({ options: normalized, filteredOptions: normalized });
    },
    handleChange: (value: string) => {
      const { options } = get();
      const keyword = removeVietnameseTones(value?.trim().toLowerCase());
      const matches = matchSorter(options, keyword, {
        keys: ['normalizedLabel'],
        threshold: matchSorter.rankings.CONTAINS,
      });

      set({ keyword: value, filteredOptions: matches });
    },
  }));
}

function createFilterStoreFull() {
  return createStore<FilterStore>((set, get) => ({
    keyword: '',
    options: [],
    filteredOptions: [],
    setOptions: (options: FilterOption[]) => {
      const normalized = normalizeOptions(options);
      set({ options: normalized, filteredOptions: normalized });
    },
    handleChange: (value: string) => {
      const { options } = get();
      const keyword = removeVietnameseTones(value?.trim().toLowerCase());
      const matches = matchSorter(options, keyword, {
        keys: ['normalizedLabel', 'normalizedValue', 'normalizedCode'],
        threshold: matchSorter.rankings.CONTAINS,
      });

      set({ keyword: value, filteredOptions: matches });
    },
  }));
}

const getFilterStoreByKey = (key: string | symbol) => {
  if (!storeRegistry.has(key)) {
    storeRegistry.set(key, createFilterStore());
  }
  return storeRegistry.get(key)!;
};

const getFilterStoreByKeyFull = (key: string | symbol) => {
  if (!storeRegistryFull.has(key)) {
    storeRegistryFull.set(key, createFilterStoreFull());
  }
  return storeRegistryFull.get(key)!;
};

export function useFilterStore(key: string | symbol, data: FilterOption[]) {
  const store = getFilterStoreByKey(key);
  const alreadySet = useStore(
    store,
    (state: FilterStore) => state.options.length > 0,
  );

  useLayoutEffect(() => {
    if (!alreadySet && data.length > 0) {
      const sorted = matchSorter(data, '', {
        keys: ['label'],
      });
      store.getState().setOptions(sorted);
    }
  }, [alreadySet, data, store]);

  return {
    keyword: useStore(store, (state: FilterStore) => state.keyword),
    filteredOptions: useStore(
      store,
      (state: FilterStore) => state.filteredOptions,
    ),
    handleChange: useStore(store, (state: FilterStore) => state.handleChange),
    setOptions: store.getState().setOptions,
  };
}

export function useFilterStoreLabelAndValue(
  key: string | symbol,
  data: FilterOption[],
) {
  const store = getFilterStoreByKeyFull(key);
  const alreadySet = useStore(
    store,
    (state: FilterStore) => state.options.length > 0,
  );

  useLayoutEffect(() => {
    if (!alreadySet && data.length > 0) {
      const sorted = matchSorter(data, '', {
        keys: ['label', 'value', 'code'],
      });
      store.getState().setOptions(sorted);
    }
  }, [alreadySet, data, store]);

  return {
    keyword: useStore(store, (state: FilterStore) => state.keyword),
    filteredOptions: useStore(
      store,
      (state: FilterStore) => state.filteredOptions,
    ),
    handleChange: useStore(store, (state: FilterStore) => state.handleChange),
    setOptions: store.getState().setOptions,
  };
}
